import { filterSuggestionItems } from '@blocknote/core/extensions';
import type { BlockNoteEditor } from '@blocknote/core';
import {
  getDefaultReactSlashMenuItems,
  SuggestionMenuController,
  useBlockNoteEditor,
} from '@blocknote/react';
import { useRouter } from 'expo-router';

import { useSidebar } from '@/components/layout/SidebarContext';
import { invalidateLinkablePagesCache } from '@/lib/blocks/page-link-search';
import type { BlockRecord } from '@/lib/blocks/types';

import { useEditorDatabaseViewPicker } from './editor-database-view-picker-context.web';
import { useEditorLinkPicker } from './editor-link-picker-context.web';
import { useEditorPageId } from './editor-page-context.web';
import { insertDatabaseFromSlash } from './insert-database-from-slash.web';
import { slashMenuFloatingOptions, slashMenuPortalElement } from './slash-menu-floating.web';

const linkIcon = (
  <svg
    fill="none"
    height="16"
    stroke="currentColor"
    strokeLinecap="round"
    strokeLinejoin="round"
    strokeWidth="2"
    viewBox="0 0 24 24"
    width="16"
  >
    <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
    <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
  </svg>
);

const databaseIcon = (
  <svg fill="none" height="16" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" width="16">
    <rect height="7" rx="1" width="18" x="3" y="3" />
    <rect height="7" rx="1" width="18" x="3" y="14" />
    <path d="M3 10h18M8 3v7M16 3v7M8 14v7M16 14v7" />
  </svg>
);

export function CouplespaceSlashMenuController() {
  const editor = useBlockNoteEditor();
  const { open } = useEditorLinkPicker();
  const { open: openDatabaseViewPicker } = useEditorDatabaseViewPicker();
  const pageId = useEditorPageId();
  const router = useRouter();
  const { addChild } = useSidebar();

  function onDatabaseCreated(block: BlockRecord, parentId: string) {
    addChild(parentId, block);
    invalidateLinkablePagesCache();
  }

  return (
    <SuggestionMenuController
      triggerCharacter="/"
      portalElement={slashMenuPortalElement}
      floatingUIOptions={slashMenuFloatingOptions}
      getItems={async (query) => {
        const typedEditor = editor as BlockNoteEditor;

        const databaseItems = [
          {
            title: 'Database',
            subtext: 'Inline table embedded in this page',
            onItemClick: () => {
              typedEditor.focus();
              void insertDatabaseFromSlash(typedEditor, pageId, {
                mode: 'inline',
                onCreated: onDatabaseCreated,
              });
            },
            group: 'Database (inline)',
            icon: databaseIcon,
            aliases: ['database', 'data', 'table', 'db', 'grid', 'inline database'],
            key: 'database_inline',
          },
          {
            title: 'Database view',
            subtext: 'Embed an existing database as a table on this page',
            onItemClick: () => {
              typedEditor.focus();
              openDatabaseViewPicker();
            },
            group: 'Database (embed)',
            icon: databaseIcon,
            aliases: [
              'linked database',
              'database view',
              'existing database',
              'embed database',
            ],
            key: 'database_view',
          },
          {
            title: 'Database — open',
            subtext: 'Create a database on its own page and open it',
            onItemClick: () => {
              typedEditor.focus();
              void insertDatabaseFromSlash(typedEditor, pageId, {
                mode: 'link',
                onCreated: onDatabaseCreated,
                navigateToDatabase: (databaseId) => {
                  router.push(`/database/${databaseId}`);
                },
              });
            },
            group: 'Database (page)',
            icon: databaseIcon,
            aliases: ['database page', 'full page database', 'open database'],
            key: 'database_open',
          },
          {
            title: 'Link to page',
            subtext: 'Link selected text to another page',
            onItemClick: () => {
              typedEditor.focus();
              open();
            },
            group: 'Other',
            icon: linkIcon,
            aliases: ['link', 'page', 'mention'],
            key: 'link_to_page',
          },
        ];

        return filterSuggestionItems(
          [...databaseItems, ...getDefaultReactSlashMenuItems(typedEditor)],
          query,
        );
      }}
    />
  );
}
